import { useEffect } from "react";
import MainLayout from "../../layouts/MainLayout";
import { usePermissions } from "../../hooks/usePermissions";
import { useNavigate, useLocation } from "react-router-dom";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import useNotification from "../../hooks/useNotification";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import PageHeader from "../../components/PageHeader";
import ListHeader from "../../components/ListHeader";
import useAction from "../../hooks/useAction";
import useUrlFilters from "../../hooks/useUrlFilters";
import useGenericFilter from "../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../components/AutoCompleteFilter";
import baseService from "../../services/baseService";
import FilterCollapse from "../../components/FilterCollapse";
import useFilterHandlers from "../../hooks/useFilterHandlers";
import TableWithInactiveFilter from "../../components/table/TableWithInactiveFilter";
import { Box } from "@mui/material";
import useQueryWithLoader from "../../hooks/useQueryWithLoader";

const RolePage = () => {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const { showNotification } = useNotification();
  const { get: baseServiceGet } = useBaseService(navigate);

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      name: [],
    },
    "roles",
  );

  const {
    openModalConfirmation,
    action,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        name: [],
      },
      handleFilterApply,
      handleFilterClear,
    );

  const {
    data: rolesResponseData,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
    isError: isErrorRoles,
    error: rolesQueryError,
  } = useQueryWithLoader({
    queryKey: ["roles", filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.roles.get, params);
      return response.result || [];
    },
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        roles: data.data.map((role) => ({
          id: role.id,
          name: role.name,
          isDeleted: !!role.deleted_at, // Booleano para controle de ações
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { roles = [], totalPages = 1 } = rolesResponseData || {};

  useEffect(() => {
    if (isErrorRoles && rolesQueryError) {
      console.error("Erro ao buscar cargos:", rolesQueryError);
      showNotification(
        "error",
        `Erro ao buscar cargos: ${rolesQueryError?.message}`,
      );
      navigate("/aluguel/cargos", { replace: true });
    }
  }, [
    isErrorRoles,
    rolesResponseData,
    isLoadingRoles,
    rolesQueryError,
    showNotification,
    navigate,
  ]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification("error", location.state.message);
    }
  }, [location.state, showNotification]);

  const columns = [{ key: "name", label: "Nome" }];

  const actions = [
    {
      id: "edit",
      icon: faEdit,
      title: "Editar",
      buttonClass: "btn-primary",
      permission: "Atualizar cargos",
      onClick: (role) => navigate(`/aluguel/cargos/editar/${role.id}`),
      show: (role) => !role.isDeleted && canAccess("Atualizar cargos"),
    },
  ];

  const inputsfilters = [
    {
      label: "Número",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="id"
          model="role"
          value={localFilterValues.id}
          onChange={(selected) => handleFilterChange("id", selected)}
          placeholder="Filtre os cargos pelo número"
          isMulti
          isClearable
        />
      ),
    },
    {
      label: "Nome",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="name"
          model="role"
          value={localFilterValues.name}
          onChange={(selected) => handleFilterChange("name", selected)}
          placeholder="Filtre os cargos pelo nome"
          isMulti
          isClearable
        />
      ),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Cargos" showBackButton={true} backUrl="/dashboard" />

      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Cargos"
          buttonText="Novo Cargo"
          buttonLink="/aluguel/cargos/criar"
          canAccess={canAccess}
          permission="Criar cargos"
        />

        <Box sx={{ mt: 2 }}>
          <TableWithInactiveFilter
            data={roles}
            columns={columns}
            actions={actions}
            totalPages={totalPages}
            currentPage={pagination.page - 1}
            onPageChange={changePage}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPagination({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValues}
            localFilterValues={localFilterValues}
            applyUrlFilters={applyUrlFilters}
            showFilterInactive={false}
            type="default"
            isLoading={isLoadingRoles}
            isFetching={isFetchingRoles}
          />
        </Box>

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default RolePage;
